import React, { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { 
  Link2, Target, TrendingUp, Search, Filter, ExternalLink, 
  FileText, Building2, Star, ArrowUpRight, BarChart3,
  Globe, Megaphone, Mail, CheckCircle2
} from "lucide-react";

// Parse keyword data from CSV structure
interface KeywordData {
  category: string;
  keyword: string;
  searchIntent: string;
  searchVolume: string;
  competitionLevel: string;
  relevanceScore: number;
  contentOpportunity: string;
  sourceType: string;
  domain: string;
  linkStrategy: string;
}

// Parsed keywords from the uploaded CSV
const keywordData: KeywordData[] = [
  // High-value seed keywords
  { category: "Smart Toilet", keyword: "Smart Toilet", searchIntent: "Commercial/Transactional", searchVolume: "Very High", competitionLevel: "Very High", relevanceScore: 5, contentOpportunity: "", sourceType: "", domain: "", linkStrategy: "" },
  { category: "Smart Toilet", keyword: "Intelligent Toilet", searchIntent: "Commercial/Transactional", searchVolume: "High", competitionLevel: "High", relevanceScore: 5, contentOpportunity: "", sourceType: "", domain: "", linkStrategy: "" },
  { category: "Smart Toilet", keyword: "Bidet Toilet", searchIntent: "Commercial/Transactional", searchVolume: "High", competitionLevel: "High", relevanceScore: 4, contentOpportunity: "", sourceType: "", domain: "", linkStrategy: "" },
  { category: "Smart Toilet", keyword: "Electric Bidet", searchIntent: "Commercial/Transactional", searchVolume: "High", competitionLevel: "Medium", relevanceScore: 4, contentOpportunity: "", sourceType: "", domain: "", linkStrategy: "" },
  { category: "Smart Toilet", keyword: "Heated Toilet Seat", searchIntent: "Product/Commercial", searchVolume: "Very High", competitionLevel: "High", relevanceScore: 3, contentOpportunity: "", sourceType: "", domain: "", linkStrategy: "" },
  { category: "Smart Toilet", keyword: "Integrated Bidet Toilet", searchIntent: "Commercial/Product", searchVolume: "Medium", competitionLevel: "Medium", relevanceScore: 5, contentOpportunity: "", sourceType: "", domain: "", linkStrategy: "" },
  { category: "Smart Toilet", keyword: "Best Smart Toilet", searchIntent: "Commercial/Review", searchVolume: "High", competitionLevel: "High", relevanceScore: 5, contentOpportunity: "", sourceType: "", domain: "", linkStrategy: "" },
  { category: "Smart Toilet", keyword: "Luxury Toilet", searchIntent: "Informational/Commercial", searchVolume: "Medium", competitionLevel: "Medium", relevanceScore: 3, contentOpportunity: "", sourceType: "", domain: "", linkStrategy: "" },
  
  // Long-tail variations
  { category: "Smart Toilet", keyword: "best smart toilet brand for small bathrooms", searchIntent: "Commercial/Review", searchVolume: "High", competitionLevel: "High", relevanceScore: 5, contentOpportunity: "Product comparison list focusing on size and features", sourceType: "", domain: "", linkStrategy: "" },
  { category: "Smart Toilet", keyword: "smart toilet vs bidet seat comparison 2025", searchIntent: "Commercial/Comparison", searchVolume: "High", competitionLevel: "High", relevanceScore: 5, contentOpportunity: "Detailed review post analyzing pros and cons", sourceType: "", domain: "", linkStrategy: "" },
  { category: "Smart Toilet", keyword: "smart toilets €600 per unit review", searchIntent: "Commercial/Price", searchVolume: "High", competitionLevel: "High", relevanceScore: 5, contentOpportunity: "Budget-focused roundup of high-value smart toilets", sourceType: "", domain: "", linkStrategy: "" },
  { category: "Smart Toilet", keyword: "is a smart toilet worth the money for new build", searchIntent: "Informational/Opinion", searchVolume: "High", competitionLevel: "High", relevanceScore: 5, contentOpportunity: "Informative article on ROI and long-term value", sourceType: "", domain: "", linkStrategy: "" },
  { category: "Smart Toilet", keyword: "plumbing requirements for integrated bidet toilet", searchIntent: "Informational/Guide", searchVolume: "Medium", competitionLevel: "Medium", relevanceScore: 5, contentOpportunity: "Technical guide for contractors and DIY installation", sourceType: "", domain: "", linkStrategy: "" },
  { category: "Smart Toilet", keyword: "best luxury japanese toilet brands for 2025", searchIntent: "Commercial/Review", searchVolume: "High", competitionLevel: "Medium", relevanceScore: 4, contentOpportunity: "High-end review targeting premium buyers", sourceType: "", domain: "", linkStrategy: "" },
  
  // Backlink targets
  { category: "Smart Home/Bathroom", keyword: "", searchIntent: "", searchVolume: "", competitionLevel: "", relevanceScore: 0, contentOpportunity: "", sourceType: "Review Site", domain: "trustpilot.com", linkStrategy: "Ensure strong brand monitoring and engagement" },
  { category: "Smart Home/Bathroom", keyword: "", searchIntent: "", searchVolume: "", competitionLevel: "", relevanceScore: 0, contentOpportunity: "", sourceType: "Top-Tier Home & Lifestyle", domain: "The Spruce", linkStrategy: "Pitch for product placement or expert quotes in Best of lists" },
  { category: "Smart Home/Bathroom", keyword: "", searchIntent: "", searchVolume: "", competitionLevel: "", relevanceScore: 0, contentOpportunity: "", sourceType: "Reviews & Consumer Tech", domain: "Tom's Guide and CNET", linkStrategy: "Offer product samples for in-depth review" },
  { category: "Smart Home/Bathroom", keyword: "", searchIntent: "", searchVolume: "", competitionLevel: "", relevanceScore: 0, contentOpportunity: "", sourceType: "E-commerce/Brands", domain: "Amazon, Home Depot, Wayfair", linkStrategy: "Focus on strong product page optimization" },
  { category: "Smart Home/Bathroom", keyword: "", searchIntent: "", searchVolume: "", competitionLevel: "", relevanceScore: 0, contentOpportunity: "", sourceType: "Architecture/Design", domain: "Architectural Digest, Elle Decor", linkStrategy: "Pitch design-focused trend articles" },
  { category: "Smart Home/Bathroom", keyword: "", searchIntent: "", searchVolume: "", competitionLevel: "", relevanceScore: 0, contentOpportunity: "", sourceType: "DIY & Remodeling", domain: "This Old House, Family Handyman", linkStrategy: "Offer detailed installation guides as guest content" },
  { category: "Smart Toilet", keyword: "", searchIntent: "", searchVolume: "", competitionLevel: "", relevanceScore: 0, contentOpportunity: "", sourceType: "Specialized Review", domain: "Many Bidets", linkStrategy: "Offer deep-dive troubleshooting or feature articles" },
  { category: "Smart Toilet", keyword: "", searchIntent: "", searchVolume: "", competitionLevel: "", relevanceScore: 0, contentOpportunity: "", sourceType: "Luxury Fixtures", domain: "REUTER", linkStrategy: "Pitch articles on high-end collections" },
  { category: "Smart Home/Bathroom", keyword: "", searchIntent: "", searchVolume: "", competitionLevel: "", relevanceScore: 0, contentOpportunity: "", sourceType: "Medical/Health", domain: "Senior care technology sites", linkStrategy: "Articles on accessibility and aging-in-place features" },
  { category: "Smart Home/Bathroom", keyword: "", searchIntent: "", searchVolume: "", competitionLevel: "", relevanceScore: 0, contentOpportunity: "", sourceType: "Sustainability Blogs", domain: "Water-saving focused sites", linkStrategy: "Guest post detailing water conservation statistics" },
];

// Separate keywords and backlink targets
const keywords = keywordData.filter(k => k.keyword !== "");
const backlinkTargets = keywordData.filter(k => k.domain !== "");

// Outreach status tracking
interface OutreachStatus {
  domain: string;
  status: "not_started" | "contacted" | "in_progress" | "secured" | "declined";
  lastContact?: string;
  notes?: string;
}

const getVolumeColor = (volume: string) => {
  switch (volume) {
    case "Very High": return "bg-green-500";
    case "High": return "bg-blue-500";
    case "Medium": return "bg-yellow-500";
    case "Low": return "bg-gray-400";
    default: return "bg-gray-300";
  }
};

const getCompetitionColor = (level: string) => {
  switch (level) {
    case "Very High": return "text-red-600 bg-red-50";
    case "High": return "text-orange-600 bg-orange-50";
    case "Medium": return "text-yellow-600 bg-yellow-50";
    case "Low": return "text-green-600 bg-green-50";
    default: return "text-gray-600 bg-gray-50";
  }
};

const getRelevanceBadge = (score: number) => {
  if (score >= 5) return { label: "Perfect Fit", color: "bg-green-500" };
  if (score >= 4) return { label: "Great Fit", color: "bg-blue-500" };
  if (score >= 3) return { label: "Good Fit", color: "bg-yellow-500" };
  return { label: "Low Fit", color: "bg-gray-400" };
};

export const LinkBuildingDashboard: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [volumeFilter, setVolumeFilter] = useState<string>("all");
  const [intentFilter, setIntentFilter] = useState<string>("all");
  const [activeTab, setActiveTab] = useState("keywords");

  // Filter keywords
  const filteredKeywords = useMemo(() => {
    return keywords.filter(k => {
      const matchesSearch = k.keyword.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesVolume = volumeFilter === "all" || k.searchVolume === volumeFilter;
      const matchesIntent = intentFilter === "all" || k.searchIntent.includes(intentFilter);
      return matchesSearch && matchesVolume && matchesIntent;
    });
  }, [searchTerm, volumeFilter, intentFilter]);

  // Stats calculations
  const stats = useMemo(() => ({
    totalKeywords: keywords.length,
    highRelevance: keywords.filter(k => k.relevanceScore >= 4).length,
    backlinkOpportunities: backlinkTargets.length,
    contentGaps: keywords.filter(k => k.contentOpportunity !== "").length,
  }), []);

  // Unique intents for filter
  const uniqueIntents = useMemo(() => {
    const intents = new Set<string>();
    keywords.forEach(k => {
      if (k.searchIntent) {
        k.searchIntent.split("/").forEach(i => intents.add(i.trim()));
      }
    });
    return Array.from(intents);
  }, []);

  return (
    <div className="space-y-8">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-primary/10 rounded-lg">
                <Target className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.totalKeywords}</p>
                <p className="text-sm text-muted-foreground">Target Keywords</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-500/10 rounded-lg">
                <Star className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.highRelevance}</p>
                <p className="text-sm text-muted-foreground">High Relevance</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-500/10 rounded-lg">
                <Link2 className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.backlinkOpportunities}</p>
                <p className="text-sm text-muted-foreground">Backlink Targets</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-accent/10 rounded-lg">
                <FileText className="h-6 w-6 text-accent" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.contentGaps}</p>
                <p className="text-sm text-muted-foreground">Content Opportunities</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Dashboard */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="keywords" className="flex items-center gap-2">
            <Search className="h-4 w-4" />
            Keywords
          </TabsTrigger>
          <TabsTrigger value="backlinks" className="flex items-center gap-2">
            <Link2 className="h-4 w-4" />
            Backlink Targets
          </TabsTrigger>
          <TabsTrigger value="content" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Content Opportunities
          </TabsTrigger>
        </TabsList>

        {/* Keywords Tab */}
        <TabsContent value="keywords" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex-1">
                  <CardTitle>Target Keywords</CardTitle>
                  <CardDescription>
                    SEO keywords categorized by search volume and competition
                  </CardDescription>
                </div>
                <div className="flex flex-wrap gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search keywords..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 w-[200px]"
                    />
                  </div>
                  <Select value={volumeFilter} onValueChange={setVolumeFilter}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder="Volume" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Volumes</SelectItem>
                      <SelectItem value="Very High">Very High</SelectItem>
                      <SelectItem value="High">High</SelectItem>
                      <SelectItem value="Medium">Medium</SelectItem>
                      <SelectItem value="Low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={intentFilter} onValueChange={setIntentFilter}>
                    <SelectTrigger className="w-[150px]">
                      <SelectValue placeholder="Intent" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Intents</SelectItem>
                      {uniqueIntents.map(intent => (
                        <SelectItem key={intent} value={intent}>{intent}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Keyword</TableHead>
                      <TableHead>Search Intent</TableHead>
                      <TableHead>Volume</TableHead>
                      <TableHead>Competition</TableHead>
                      <TableHead>Relevance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredKeywords.map((kw, index) => {
                      const relevance = getRelevanceBadge(kw.relevanceScore);
                      return (
                        <TableRow key={index}>
                          <TableCell className="font-medium">{kw.keyword}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">
                              {kw.searchIntent}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className={`w-2 h-2 rounded-full ${getVolumeColor(kw.searchVolume)}`} />
                              <span className="text-sm">{kw.searchVolume}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary" className={getCompetitionColor(kw.competitionLevel)}>
                              {kw.competitionLevel}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={`${relevance.color} text-white`}>
                              {relevance.label}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Backlinks Tab */}
        <TabsContent value="backlinks" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                Backlink Outreach Targets
              </CardTitle>
              <CardDescription>
                High-authority domains for link building and partnerships
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {backlinkTargets.map((target, index) => (
                  <Card key={index} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <Badge variant="outline" className="text-xs">
                          {target.sourceType}
                        </Badge>
                        <Button variant="ghost" size="icon" className="h-6 w-6">
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      </div>
                      <h4 className="font-semibold text-sm mb-2">{target.domain}</h4>
                      <p className="text-xs text-muted-foreground mb-3">
                        {target.linkStrategy}
                      </p>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="text-xs">
                          {target.category}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
          
          {/* Outreach Pipeline */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Megaphone className="h-5 w-5" />
                Outreach Pipeline
              </CardTitle>
              <CardDescription>
                Track your link building outreach progress
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                <div className="text-center p-4 bg-muted rounded-lg">
                  <p className="text-2xl font-bold">{backlinkTargets.length}</p>
                  <p className="text-xs text-muted-foreground">Not Started</p>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-2xl font-bold text-blue-600">0</p>
                  <p className="text-xs text-muted-foreground">Contacted</p>
                </div>
                <div className="text-center p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <p className="text-2xl font-bold text-yellow-600">0</p>
                  <p className="text-xs text-muted-foreground">In Progress</p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-2xl font-bold text-green-600">0</p>
                  <p className="text-xs text-muted-foreground">Secured</p>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-2xl font-bold text-red-600">0</p>
                  <p className="text-xs text-muted-foreground">Declined</p>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Overall Progress</span>
                  <span>0%</span>
                </div>
                <Progress value={0} className="h-2" />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Content Opportunities Tab */}
        <TabsContent value="content" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Content Opportunities
              </CardTitle>
              <CardDescription>
                Identified gaps for content creation based on keyword research
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {keywords.filter(k => k.contentOpportunity !== "").map((kw, index) => (
                  <Card key={index} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="font-semibold text-sm">{kw.keyword}</h4>
                            <Badge variant="outline" className="text-xs">
                              {kw.searchIntent}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mb-3">
                            {kw.contentOpportunity}
                          </p>
                          <div className="flex items-center gap-4 text-xs">
                            <div className="flex items-center gap-1">
                              <BarChart3 className="h-3 w-3" />
                              <span>{kw.searchVolume} volume</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <TrendingUp className="h-3 w-3" />
                              <span>{kw.competitionLevel} competition</span>
                            </div>
                          </div>
                        </div>
                        <Badge className={`${getRelevanceBadge(kw.relevanceScore).color} text-white`}>
                          Score: {kw.relevanceScore}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Content Strategy Summary */}
          <Card>
            <CardHeader>
              <CardTitle>Recommended Content Strategy</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                    <h4 className="font-semibold text-green-800">Quick Wins</h4>
                  </div>
                  <p className="text-sm text-green-700">
                    Product comparison posts, installation guides, and FAQ content for medium competition keywords.
                  </p>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="h-5 w-5 text-blue-600" />
                    <h4 className="font-semibold text-blue-800">Growth Focus</h4>
                  </div>
                  <p className="text-sm text-blue-700">
                    In-depth review articles and brand comparison content targeting high volume keywords.
                  </p>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Star className="h-5 w-5 text-purple-600" />
                    <h4 className="font-semibold text-purple-800">Authority Building</h4>
                  </div>
                  <p className="text-sm text-purple-700">
                    Technical guides, industry insights, and thought leadership for expert positioning.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default LinkBuildingDashboard;
